<template>
    <main class="p-2 w-full h-full max-h-main flex flex-col items-start min-h-main">
        <div class="w-full">
            <div class="flex w-full gap-2 items-center">
                <app-button @click="addCie()" :label="$t('cie.newCie')" v-if="!readOnly" />
                <div class="border px-1">
                    <table>
                        <tr>
                            <th class="p-1">{{ $t('cie.cieBalance') }}:</th>
                            <td class="p-1">
                                {{
                                    cieBalance > 0
                                        ? '+' + $n(cieBalance, currencyFormat)
                                        : cieBalance < 0
                                        ? $n(cieBalance, currencyFormat)
                                        : '0.00'
                                }}
                            </td>
                        </tr>
                    </table>
                </div>
                <span class="flex-grow"></span>
                <a
                    :href="lastCIEExport ? lastCIEExport.url : '#'"
                    ref="lastPdfLink"
                    download
                    target="_blank"
                    class="underline text-xs"
                    v-if="lastCIEExport && lastCIEExport.generationSucceeded"
                    :title="$t('commons.lastExport') + ' (' + humanizeDate(lastCIEExport.createdAt) + ')'"
                >
                    {{ $t('commons.lastExport') }}
                </a>
                <app-button
                    size="mini"
                    class="p-2 shadow"
                    v-click-helper="openPdf"
                    :aria-label="$t('commons.toPdf')"
                    :title="$t('commons.toPdf')"
                    :disabled="lastCIEExport && !lastCIEExport.generationEndDate"
                    :icon="
                        lastCIEExport && !lastCIEExport.generationEndDate
                            ? 'icon-rotate-right'
                            : 'icon-file-pdf-outline'
                    "
                    :icon-class="lastCIEExport && !lastCIEExport.generationEndDate ? 'animate animate-spin' : ''"
                />
            </div>
            <app-multi-picker
                ref="filter"
                icon="icon-magnify"
                :allowStringCriteria="true"
                class="w-full my-2"
                v-model="filterValue"
                :options="filterOptions"
                :strictMatching="true"
            >
                <template v-slot:option="{ option }">
                    <span>{{ option.fullName || option.name }}</span>
                    <span class="text-xs text-gray-600 ml-1">{{ option.criteriaType }}</span>
                </template>
            </app-multi-picker>
            <div class="flex justify-between">
                <div>
                    <app-select @input="onAction" v-if="!loading && selection.length > 0 && !readOnly" class="text-xs">
                        <option value="" disabled selected>{{ $t('commons.actions') }}</option>
                        <option value="delete">{{ $t('commons.actionDelete') }}</option>
                    </app-select>
                </div>
            </div>
        </div>
        <div class="flex w-full flex-grow overflow-auto flex-col text-xs relative">
            <div v-if="loading" class="flex justify-center">
                <icon-rotate-right class="animate animate-spin"></icon-rotate-right>
            </div>
            <table class="table-fixed" v-else>
                <thead class="sticky top-0 bg-white">
                    <tr>
                        <th style="width: 2rem" class="hidden sm:table-cell text-left border-r p-1" v-if="!readOnly">
                            <app-checkbox
                                :value="selection.length === filteredItems.length && selection.length > 0"
                                :indeterminate="selection.length > 0 && selection.length < filteredItems.length"
                                :show-label="false"
                                :label="$t('commons.toggleSelectAll')"
                                @input="toggleSelectAll"
                            ></app-checkbox>
                        </th>
                        <th class="text-left border-r p-1" style="width: 15rem">
                            <button
                                class="hover:underline font-bold w-full flex justify-between"
                                @click="sortBy('emitter')"
                            >
                                {{ $t('commons.bundle') }}
                                <div v-if="sortKey === 'emitter'">
                                    <icon-menu-up v-if="sortAsc" width="16" height="16" />
                                    <icon-menu-down v-else width="16" height="16" />
                                </div>
                            </button>
                        </th>
                        <th class="text-left border-r p-1">
                            <button
                                class="hover:underline font-bold w-full flex justify-between"
                                @click="sortBy('object')"
                            >
                                {{ $t('commons.object') }}
                                <div v-if="sortKey === 'object'">
                                    <icon-menu-up v-if="sortAsc" width="16" height="16" />
                                    <icon-menu-down v-else width="16" height="16" />
                                </div>
                            </button>
                        </th>
                        <th class="text-center border-r p-1" style="width: 6rem">
                            <button
                                class="hover:underline font-bold w-full flex justify-between"
                                @click="sortBy('amountTTC')"
                            >
                                <div class="flex flex-col">
                                    <span>{{ $t('commons.amountTTC') }}</span>
                                    ({{
                                        filteredTotal > 0
                                            ? '+' + $n(filteredTotal, currencyFormat)
                                            : filteredTotal < 0
                                            ? $n(filteredTotal, currencyFormat)
                                            : '0.00'
                                    }})
                                </div>
                            </button>
                        </th>
                        <th style="width: 6rem" class="hidden sm:table-cell text-center border-r p-1">
                            <button
                                class="hover:underline font-bold w-full flex justify-between"
                                @click="sortBy('quoteStatus')"
                            >
                                {{ $t('commons.status') }}
                            </button>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <template v-for="cie in filteredItems">
                        <tr class="odd:bg-blue-50 border-t">
                            <td class="hidden sm:table-cell border-r p-1" v-if="!readOnly">
                                <a :id="'uuid_' + cie.id" style="scroll-margin-top: 3em"></a>
                                <app-checkbox
                                    :value="cie.isSelected"
                                    :label="$t('commons.select')"
                                    :show-label="false"
                                    @input="saveSelection(cie)"
                                ></app-checkbox>
                            </td>
                            <td class="text-left border-r p-1">
                                <div :title="cie.bundle && cie.bundle.company ? cie.bundle.company.name : ''">
                                    {{ cie.bundle ? cie.bundle.label : '' }}
                                </div>
                            </td>
                            <td class="border-r p-1">
                                <template v-if="cie.cieObject">
                                    <div class="flex gap-1">
                                        <span class="font-bold">{{ cie.object }}</span>
                                        <span class="">- {{ cie.bundleTo.label }} -</span>
                                        <router-link
                                            class="hover:underline flex gap-1"
                                            :to="{
                                                name: 'cieDetail',
                                                params: {
                                                    ...$route.params,
                                                    cieId: cie.id,
                                                },
                                            }"
                                        >
                                            <span>{{ cie.cieObject }}</span>
                                            <span v-if="cie.code" class="mx-1">#{{ cie.code }}</span>
                                            <span v-if="!cie.object || cie.object.trim().length === 0">...</span>
                                        </router-link>
                                    </div>
                                </template>
                                <template v-else>
                                    <router-link
                                        class="hover:underline flex gap-1"
                                        :to="{
                                            name: 'cieDetail',
                                            params: {
                                                ...$route.params,
                                                cieId: cie.id,
                                            },
                                        }"
                                    >
                                        <span class="font-bold">{{ cie.object }}</span>
                                        <span v-if="cie.code" class="mx-1">#{{ cie.code }}</span>
                                        <span v-if="!cie.object || cie.object.trim().length === 0">...</span>
                                    </router-link>
                                </template>
                            </td>
                            <td
                                class="border-r p-1 text-right"
                                :class="{
                                    'text-green-500': cie.amountTTC >= 0,
                                    'text-red-600': cie.amountTTC < 0,
                                }"
                            >
                                {{
                                    cie.amountTTC
                                        ? (cie.amountTTC > 0 ? '+' : '') + $n(cie.amountTTC, currencyFormat)
                                        : '-'
                                }}
                            </td>
                            <td class="text-left border-r p-1" :class="cie.quoteStatusColor">
                                {{ $t('amendments.quoteStatus.' + cie.quoteStatus) }}
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
        </div>
        <app-quick-actions :options="quickActions" @choose="$event.run()"></app-quick-actions>
    </main>
</template>

<script>
import { isMobile } from '@/state/state';
import { filterMatch, get, getMapById, sortBy, uniqBy } from '@/services/sanitize.service';
import AppMultiPicker from '@/components/appMultiPicker/AppMultiPicker';
import AppQuickActions from '@/components/appQuickActions/AppQuickActions';
import AppButton from '@/components/appButton/AppButton';
import IconMenuDown from '@/icons/IconMenuDown';
import IconMenuUp from '@/icons/IconMenuUp';
import { queryProject } from '@/features/projects/projects.service';
import { combineLatest } from 'rxjs';
import { getBundles } from '@/features/bundles/bundles.service';
import { getCompanies } from '@/features/companies/companies.service';
import AppMultipleUploadButton from '@/components/app-multipleUploadButton/AppMultipleUploadButton.vue';
import { error, info } from '@/features/toasts/toats.service';
import { replicateProjects } from '@/rxdb/database';
import { humanizeDate } from '@/filters/dateFilter';
import { createCIEExport, getLastCIEExport } from '@/features/cie/CIEExports.service';

import vueClickHelper from 'vue-click-helper';
import { createCie, getCIEs, removeCie } from '@/features/cie/CIE.service';
import { confirm } from '@/features/dialogs/dialogs.service';
import AppSelect from '@/components/appSelect/AppSelect.vue';
import AppCheckbox from '@/components/app-checkbox/AppCheckbox.vue';

export default {
    components: {
        AppCheckbox,
        AppSelect,
        AppMultipleUploadButton,
        IconMenuUp,
        IconMenuDown,
        AppButton,
        AppQuickActions,
        AppMultiPicker,
    },
    directives: { 'click-helper': vueClickHelper },
    async created() {
        this.restoreFilter();
        queryProject(this.$route.params.projectId).then((project) => {
            this.readOnly = !project.me.allowedFeatures.includes('project_amendments');
            this.currencyFormat.currency = project.currency;
        });
        this.init();
    },
    computed: {
        filteredTotal() {
            return (
                Math.round(
                    this.filteredItems.reduce((acc, item) => (item.amountTTC ? item.amountTTC + acc : acc), 0) * 100,
                ) / 100
            );
        },
        cieBalance() {
            return (
                Math.round(
                    this.items
                        .filter((item) => item.quoteStatus === 'validated')
                        .reduce((acc, item) => (item.amountTTC ? item.amountTTC + acc : acc), 0) * 100,
                ) / 100
            );
        },
        filteredItems() {
            let result = this.filterFn(this.filterValue);
            if (this.sortKey) {
                result = sortBy(result, (item) => {
                    if (this.sortKey === 'object') {
                        return item.object;
                    } else if (this.sortKey === 'amountTTC') {
                        return item.amountTTC;
                    } else if (this.sortKey === 'quoteStatus') {
                        return item.quoteStatus;
                    } else if (this.sortKey === 'emitter') {
                        return item.bundle ? item.bundle.label : null;
                    }
                });
                if (!this.sortAsc) {
                    result.reverse();
                }
            }
            return result;
        },
        filterOptions() {
            return [
                {
                    isGroup: true,
                    name: this.$t('commons.status'),
                    id: this.$t('commons.status'),
                    children: [
                        {
                            id: 'quote_coming',
                            name: this.$t('amendments.comingQuotes'),
                            firstOfCriteriaType: false,
                            _isStateCriteria: true,
                        },
                        {
                            id: 'quote_toEmit',
                            name: this.$t('amendments.quoteToEmit'),
                            firstOfCriteriaType: false,
                            _isStateCriteria: true,
                        },
                        {
                            id: 'quote_toAnalyse',
                            name: this.$t('amendments.quoteToAnalyse'),
                            firstOfCriteriaType: false,
                            _isStateCriteria: true,
                        },
                        {
                            id: 'quote_toNegotiate',
                            name: this.$t('amendments.quoteToNegotiate'),
                            firstOfCriteriaType: false,
                            _isStateCriteria: true,
                        },
                        {
                            id: 'quote_toValidateMOA',
                            name: this.$t('amendments.quoteToValidateMOA'),
                            firstOfCriteriaType: false,
                            _isStateCriteria: true,
                        },
                        {
                            id: 'quote_validated',
                            name: this.$t('amendments.quoteValidated'),
                            firstOfCriteriaType: false,
                            _isStateCriteria: true,
                        },
                        {
                            id: 'quote_rejected',
                            name: this.$t('amendments.quoteRejected'),
                            firstOfCriteriaType: false,
                            _isStateCriteria: true,
                        },
                        {
                            id: 'quote_obsolete',
                            name: this.$t('amendments.quoteObsolete'),
                            firstOfCriteriaType: false,
                            _isStateCriteria: true,
                        },
                        {
                            id: 'quote_isMarket',
                            name: this.$t('amendments.quoteIsMarket'),
                            firstOfCriteriaType: false,
                            _isStateCriteria: true,
                        },
                        {
                            id: 'quote_isMarketOption',
                            name: this.$t('amendments.quoteIsMarketOption'),
                            firstOfCriteriaType: false,
                            _isStateCriteria: true,
                        },
                        {
                            id: 'quote_noImpact',
                            name: this.$t('amendments.quoteNoImpact'),
                            firstOfCriteriaType: false,
                            _isStateCriteria: true,
                        },
                    ],
                },
                {
                    isGroup: true,
                    name: this.$t('commons.type'),
                    id: this.$t('commons.type'),
                    children: [
                        {
                            id: 'cie',
                            name: this.$t('cie.CIE'),
                            firstOfCriteriaType: false,
                            _isTypeCriteria: true,
                        },
                        {
                            id: 'engagedCIE',
                            name: this.$t('cie.engagedCIEShort'),
                            firstOfCriteriaType: false,
                            _isTypeCriteria: true,
                        },
                    ],
                },
                {
                    isGroup: true,
                    name: this.$t('commons.emitterCriteriaType'),
                    id: this.$t('commons.emitterCriteriaType'),
                    children: this.bundles.map((bundle, index) => ({
                        ...bundle,
                        name: bundle.label,
                        firstOfCriteriaType: index === 0,
                        criteriaType: this.$t('commons.emitterCriteriaType'),
                        _isBundleCriteria: true,
                    })),
                },
            ];
        },
    },
    methods: {
        humanizeDate,
        init() {
            this.subscriptions = [
                combineLatest([
                    getBundles(this.$route.params.projectId),
                    getCIEs(this.$route.params.projectId),
                    getCompanies(this.$route.params.projectId),
                ]).subscribe(async ([bundles, cies, companies]) => {
                    this.companies = companies.map((company) => ({
                        ...company,
                        bundleIds: bundles
                            .filter((bundle) => bundle.companyId === company.id)
                            .map((bundle) => bundle.id),
                    }));
                    const companyMap = getMapById(companies);
                    this.bundles = sortBy(
                        bundles.map((bundle) => ({ ...bundle, company: companyMap[bundle.companyId] })),
                        (bundle) => bundle.label,
                    );
                    this.bundleMap = getMapById(this.bundles);
                    this.items = uniqBy(cies, 'groupId')
                        .map((cie) => {
                            const bundle = this.bundleMap[cie.bundleId];
                            return {
                                ...cie,
                                bundle,
                                filterString: [cie.code, cie.object].join(),
                                quoteStatusColor: this.quoteStatusColors[cie.quoteStatus] || '',
                                isSelected: this.selection.includes(cie.id),
                            };
                        })
                        .reduce(
                            (acc, cie) => [
                                ...acc,
                                cie,
                                ...cie.distribution.map((distribution) => ({
                                    id: cie.id,
                                    type: 'engagedCIE',
                                    bundleId: distribution.bundleId,
                                    bundle: this.bundleMap[distribution.bundleId],
                                    bundleTo: this.bundleMap[cie.bundleId],
                                    amountTTC: -distribution.amount,
                                    quoteStatus: cie.quoteStatus,
                                    filterString: [cie.code, cie.object].join(),
                                    object: this.$t('cie.engagedCIEShort'),
                                    cieObject: cie.object,
                                    quoteStatusColor: this.quoteStatusColors[cie.quoteStatus] || '',
                                    code: cie.code,
                                })),
                            ],
                            [],
                        );
                    this.lastCIEExport = await getLastCIEExport(this.$route.params.projectId);
                    this.cleanupSavedSelection();
                    this.scrollToLastVisited();
                    this.loading = false;
                }),
            ];
        },

        openPdf(e, isDoubleClick) {
            if (isDoubleClick) {
                this.$refs.lastPdfLink.click();
            } else {
                this.exportPDF();
            }
        },
        async exportPDF() {
            await replicateProjects(this.$route.params.projectId);
            this.lastCIEExport = { generationEndDate: null };
            const lastCIEExport = await createCIEExport(this.$route.params.projectId, {
                sortKey: this.sortKey,
                sortAsc: this.sortAsc,
                filter: this.filterValue,
            });
            if (lastCIEExport) {
                if (lastCIEExport.generationSucceeded === true) {
                    info(
                        this.$t('commons.generationSucceed') +
                            `<br/><a href="${lastCIEExport.url}"><u>Télécharger le pdf</u></a>`,
                        5000,
                    );
                } else if (lastCIEExport.generationSucceeded === false) {
                    error(this.$t('commons.generationFailed'));
                }
            }
            this.lastCIEExport = lastCIEExport;
        },
        scrollToLastVisited() {
            const lastVisitedCieId = localStorage.getItem('cie.lastVisitedCieId.' + this.$route.params.projectId);
            if (lastVisitedCieId) {
                setTimeout(() => {
                    const element = this.$el.querySelector('#uuid_' + lastVisitedCieId);
                    if (element) element.scrollIntoView();
                }, 800);
            }
        },
        get,
        sortBy(key) {
            if (key === this.sortKey) {
                this.sortAsc = !this.sortAsc;
            } else {
                this.sortKey = key;
                this.sortAsc = true;
            }
            this.saveFilter(this.filterValue);
        },
        async addCie() {
            const result = await createCie(this.$route.params.projectId, {
                object: '',
                distribution: [],
            });
            await this.$router.push({
                name: 'cieDetail',
                params: {
                    projectId: this.$route.params.projectId,
                    cieId: result.id,
                },
            });
        },
        matchBundleFilter(item, bundleIds) {
            return item.bundle && bundleIds.includes(item.bundle.id);
        },
        matchString(stringCriteria, item) {
            if (!stringCriteria || stringCriteria.length === 0) {
                return true;
            }
            return stringCriteria.find((criteria) => filterMatch(item.filterString || item.name, criteria, true));
        },
        matchState(stateCriteria, item) {
            if (!stateCriteria || stateCriteria.length === 0) {
                return true;
            }
            return stateCriteria.find(
                (criteria) =>
                    (criteria.id === 'quote_coming' && this.comingStatus.includes(item.quoteStatus)) ||
                    (item.quoteStatus && criteria.id === 'quote_' + item.quoteStatus),
            );
        },
        matchType(criteria, item) {
            if (!criteria || criteria.length === 0) {
                return true;
            }
            return criteria.find(
                (criteria) =>
                    (criteria.id === 'cie' && item.type !== 'engagedCIE') ||
                    (criteria.id === 'engagedCIE' && item.type === 'engagedCIE'),
            );
        },
        saveFilter(filterValue) {
            localStorage.setItem(
                'cies_cie_filter_' + this.$route.params.projectId,
                JSON.stringify({
                    filterValue,
                    sortKey: this.sortKey,
                }),
            );
        },
        restoreFilter() {
            const cache = localStorage.getItem('cies_cie_filter_' + this.$route.params.projectId);
            if (cache) {
                const parsedCache = JSON.parse(cache);
                this.filterValue = parsedCache.filterValue || [];
                this.sortKey = parsedCache.sortKey || 'object';
            }
        },
        filterFn(filter) {
            this.saveFilter(filter);
            const bundleIds = [...filter.filter((aCriteria) => aCriteria._isBundleCriteria).map((bundle) => bundle.id)];
            const stringCriteria = filter
                .filter((aCriteria) => aCriteria._isStringCriteria)
                .map((aCriteria) => aCriteria.content);
            const stateCriteria = filter.filter((aCriteria) => aCriteria._isStateCriteria);
            const typeCriteria = filter.filter((aCriteria) => aCriteria._isTypeCriteria);

            return this.items.filter((item) => {
                const fullCriteria = {
                    matchBundleFilter: bundleIds.length === 0 || this.matchBundleFilter(item, bundleIds),
                    matchString: this.matchString(stringCriteria, item),
                    matchState: this.matchState(stateCriteria, item),
                    typeCriteria: this.matchType(typeCriteria, item),
                };
                return Object.values(fullCriteria).every((value) => !!value);
            });
        },
        cleanupSavedSelection() {
            this.selection = this.selection.filter((itemId) => !!this.items.find((anItem) => anItem.id === itemId));
        },
        saveSelection(item) {
            if (item) {
                if (!item.isSelected) {
                    this.selection.push(item.id);
                } else {
                    this.selection = this.selection.filter((anItem) => anItem !== item.id);
                }
            }
            localStorage.setItem('cie.selection.' + this.$route.params.projectId, JSON.stringify(this.selection));
        },
        async onAction(action) {
            const selectedItems = this.items.filter((item) => this.selection.includes(item.id));
            if (action === 'delete') {
                if (await confirm(this.$t('commons.confirmMessageAll', { number: this.selection.length }))) {
                    await Promise.all(selectedItems.map((item) => removeCie(this.$route.params.projectId, item.id)));
                    this.selection = [];
                }
            }
        },
        toggleSelectAll() {
            if (this.selection.length < this.filteredItems.length) {
                this.selection = this.filteredItems.map((item) => item.id);
            } else {
                this.selection = [];
            }
            this.saveSelection();
        },
    },
    data() {
        return {
            lastCIEExport: null,
            quoteStatusColors: {
                toEmit: 'bg-amber-500',
                toAnalyse: 'text-white bg-gray-500',
                toNegotiate: '',
                toValidateMOA: 'bg-cyan-500',
                validated: 'bg-green-600',
                rejected: 'text-white bg-red-600',
                obsolete: 'text-white bg-red-600',
                market: '',
                marketOption: '',
                noImpact: '',
            },
            comingStatus: ['toEmit', 'toAnalyse', 'toNegotiate', 'toValidateMOA'],
            currencyFormat: {
                currency: 'EUR',
                currencyDisplay: 'symbol',
                style: 'currency',
                minimumFractionDigits: 2,
            },
            bundleMap: {},
            loading: 0,
            quickActions: [
                {
                    name: this.$t('cie.newCie'),
                    run: () => this.addCie(),
                },
            ],
            sortKey: 'name',
            sortAsc: true,
            readOnly: true,
            subscriptions: [],
            bundles: [],
            companies: [],
            isMobile,
            items: [],
            filterValue: [],
            selection: [],
        };
    },
};
</script>
